/**
 * BaseModal.Inline - Inline Content Plugin for BaseModal
 * Handles moving content from DOM to modal and back
 *
 * Features:
 * - Moves content instead of copying (preserves events and state)
 * - Uses placeholder comment to mark original position
 * - Automatically handles visibility
 *
 * @requires BasePlugin.js
 * @version 2.0.0
 */

'use strict';

import BasePlugin         from './BasePlugin.js';
import {waitForAnimation} from '../animations.js';
import {generateModalId}  from '../constants.js';

/**
 * @typedef {Object} InlineConfig
 * @property {string} [selector='[data-basemodal-inline]'] - CSS selector
 * @property {string} [theme='light'] - Modal theme
 * @property {string} [animation='zoom'] - Animation type
 * @property {string} [size='default'] - Modal size
 * @property {boolean} [showCloseButton=true] - Show close button
 * @property {string} [closeButtonContent='&times;'] - Close button content (text or SVG)
 * @property {boolean} [showOverlay=true] - Show overlay background
 * @property {boolean} [debugMode=false] - Debug mode
 * @property {InlineLocales} [locales] - Localization
 */

/**
 * @typedef {Object} InlineLocales
 * @property {string} [close='Close'] - Close button text
 */

/**
 * @typedef {Object} PlaceholderData
 * @property {Comment} placeholder - Comment node for original position
 * @property {HTMLElement} originalParent - Original parent element
 * @property {HTMLElement} element - Moved element
 * @property {string} originalDisplay - Original display style
 */

// ========================================
// CONSTANTS & DEFAULTS
// ========================================

/**
 * Default configuration for inline modal
 * @type {InlineConfig}
 */
const DEFAULTS = {
    selector          :'[data-basemodal-inline]',
    dataAttribute     :'data-basemodal-inline',
    closeAttribute    :'data-basemodal-close',
    theme             :'light',
    animation         :'zoom',
    size              :'default',
    showCloseButton   :true,
    closeButtonContent:'&times;',
    showOverlay       :true,
    debugMode         :false,
    locales           :{
        close:'Close'
    }
};

// ========================================
// MAIN CLASS
// ========================================

/**
 * BaseModal.Inline Class
 */
export default class BaseModalInline extends BasePlugin {
    /**
     * Create an inline plugin instance
     * @param {InlineConfig} config - Configuration options
     */
    constructor(config = {}) {
        super(config);
        this.config             = {...DEFAULTS, ...config};
        this.activePlaceholders = new Map();
    }

    // ========================================
    // INITIALIZATION
    // ========================================

    /**
     * Initialize inline plugin
     * Attaches event listeners to document for delegation
     * @public
     */
    init(baseModal) {
        super.init(baseModal);
        this.attachEventListeners();

        if (this.config.debugMode) {
            console.log('BaseModal.Inline: Event listeners attached');
        }
    }

    /**
     * Attach click event listeners using delegation
     * @private
     */
    attachEventListeners() {
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest(this.config.selector);
            if (trigger) {
                e.preventDefault();
                const contentId = trigger.getAttribute(this.config.dataAttribute);
                void this.show(contentId, trigger);
            }
        });
    }

    // ========================================
    // MODAL DISPLAY
    // ========================================

    /**
     * Show inline content in modal
     * @param {string} contentId - ID of the element to show
     * @param {HTMLElement} [trigger] - Element that triggered the modal
     * @returns {Promise<void>}
     * @public
     */
    async show(contentId, trigger = null) {
        const element = document.getElementById(contentId);

        if (!element) {
            console.error(`BaseModal.Inline: Element with ID "${contentId}" not found`);
            return;
        }

        this.restoreIfActive(element);

        const modalId = generateModalId('inline');

        if (this.config.debugMode) {
            console.log(`BaseModal.Inline: Moving element #${contentId} to modal #${modalId}`);
        }

        const placeholder    = document.createComment(`BaseModal Placeholder for #${contentId}`);
        const originalParent = element.parentNode;

        originalParent.insertBefore(placeholder, element);

        this.activePlaceholders.set(modalId, {
            placeholder,
            originalParent,
            element,
            originalDisplay:element.style.display
        });

        const modalHTML = this.generateModalHTML(modalId);
        document.body.insertAdjacentHTML('beforeend', modalHTML);

        const modal          = document.getElementById(modalId);
        const contentWrapper = modal.querySelector('.bm-inline-content-wrapper');

        contentWrapper.appendChild(element);

        element.style.display = 'block';
        element.classList.add('bm-inline-active');

        try {
            const overrides = {};
            if (trigger) {
                if (trigger.dataset.theme) overrides.theme = trigger.dataset.theme;
                if (trigger.dataset.size) overrides.size = trigger.dataset.size;
                if (trigger.dataset.animation) overrides.animation = trigger.dataset.animation;
            }

            await this.showModal(modalId, {
                theme              :overrides.theme || this.config.theme,
                animation          :overrides.animation || this.config.animation,
                size               :overrides.size || this.config.size,
                awaitCloseAnimation:true,
                onClose            :() => {
                    this.cleanup(modalId);
                }
            });

        } catch (error) {
            console.error('BaseModal.Inline: Error showing modal:', error);
            this.cleanup(modalId);
        }
    }

    // ========================================
    // CLEANUP & RESTORE
    // ========================================

    /**
     * Restore content to original position and cleanup
     * @param {string} modalId - ID of the modal
     * @private
     */
    cleanup(modalId) {
        const data = this.activePlaceholders.get(modalId);

        if (!data) {
            if (this.config.debugMode) {
                console.warn(`BaseModal.Inline: No cleanup data found for ${modalId}`);
            }
            return;
        }

        const modal = document.getElementById(modalId);

        if (!modal) {
            this.restoreContent(data);
            this.activePlaceholders.delete(modalId);
            return;
        }

        waitForAnimation(this, modal, () => {
            const currentData = this.activePlaceholders.get(modalId);

            if (!currentData) {
                if (this.config.debugMode) {
                    console.warn(`BaseModal.Inline: Skip cleanup for ${modalId} - already restored`);
                }

                modal.remove();
                return;
            }

            this.restoreContent(currentData);

            modal.remove();

            this.activePlaceholders.delete(modalId);
        });
    }

    /**
     * Restore content immediately if it is already active in another modal
     * @param {HTMLElement} element - Target element
     * @private
     */
    restoreIfActive(element) {
        const activeEntry = [...this.activePlaceholders.entries()].find(([, data]) => data.element === element);

        if (!activeEntry) {
            return;
        }

        const [activeModalId, data] = activeEntry;
        const activeModal           = document.getElementById(activeModalId);

        this.restoreContent(data);

        if (activeModal) {
            activeModal.remove();
        }

        this.activePlaceholders.delete(activeModalId);
    }

    /**
     * Helper to restore content to original position
     * @param {Object} data - Placeholder data
     * @private
     */
    restoreContent(data) {
        const {
                  placeholder,
                  originalParent,
                  element,
                  originalDisplay
              } = data;

        if (this.config.debugMode) {
            console.log(`BaseModal.Inline: Restoring element #${element.id}`);
        }

        if (originalParent.contains(placeholder)) {
            originalParent.insertBefore(element, placeholder);
            placeholder.remove();
        } else {
            originalParent.appendChild(element);
        }

        element.style.display = originalDisplay;
        element.classList.remove('bm-inline-active');
    }

    // ========================================
    // HTML GENERATION
    // ========================================

    /**
     * Generate modal HTML structure
     * @param {string} modalId - Unique modal ID
     * @returns {string} HTML string
     * @private
     */
    generateModalHTML(modalId) {
        const closeLabel = (this.config.locales && this.config.locales.close) ? this.config.locales.close : 'Close';
        const closeAttribute = this.config.closeAttribute || 'data-basemodal-close';

        const closeButton = this.config.showCloseButton ? `
            <button aria-label="${closeLabel}"
                    class="bm-modal__close"
                    ${closeAttribute}>
                ${this.config.closeButtonContent}
            </button>` : '';

        const overlay = this.config.showOverlay ? `
        <div class="bm-modal__bg" aria-hidden="true" ${closeAttribute}></div>` : '';

        return `
<div id="${modalId}" class="bm-modal bm-inline-modal" aria-hidden="true">
    <div tabindex="-1" class="bm-modal__overlay">
        <div role="dialog" class="bm-modal__container bm-modal__container--inline" aria-modal="true">${closeButton}

            <div class="bm-modal__content bm-inline-content-wrapper">
                <!-- Content will be moved here -->
            </div>
        </div>${overlay}
    </div>
</div>`;
    }

    /**
     * Destroy inline plugin instance
     * Cleans up all active placeholders
     * @public
     */
    destroy() {
        this.activePlaceholders.forEach((data) => {
            this.restoreContent(data);
        });
        this.activePlaceholders.clear();
        super.destroy();
    }
}
